import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { profileAPI } from '../../services/api'
import {
  FlagIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  ClockIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline'

// VH-62: Validation ranges for manual goal editing
const EDIT_VALIDATION = {
  target_calories: { min: 500, max: 10000 },
  target_protein_g: { min: 0, max: 1000 },
  target_carbs_g: { min: 0, max: 1000 },
  target_fat_g: { min: 0, max: 1000 }
}

const GOAL_TYPE_ICONS = {
  lose_weight: '📉',
  maintain: '⚖️',
  gain_muscle: '💪',
  custom: '🎯'
}

export default function NutritionGoalsCard() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [editValues, setEditValues] = useState({})
  const [editErrors, setEditErrors] = useState({})

  const { data: currentGoal, isLoading } = useQuery({
    queryKey: ['currentGoal'],
    queryFn: () => profileAPI.getCurrentGoal(),
    select: (response) => response.data
  })

  const updateGoalMutation = useMutation({
    mutationFn: ({ id, data }) => profileAPI.updateGoal(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currentGoal'] })
      queryClient.invalidateQueries({ queryKey: ['profileSummary'] })
      queryClient.invalidateQueries({ queryKey: ['healthCycleGoalProgress'] })
      setEditing(false)
    }
  })

  const startEdit = () => {
    if (currentGoal) {
      setEditValues({
        target_calories: currentGoal.target_calories,
        target_protein_g: currentGoal.target_protein_g,
        target_carbs_g: currentGoal.target_carbs_g,
        target_fat_g: currentGoal.target_fat_g
      })
      setEditing(true)
    }
  }

  const cancelEdit = () => {
    setEditing(false)
    setEditValues({})
    setEditErrors({})
  }

  const validateEditValues = () => {
    const errors = {}
    Object.entries(EDIT_VALIDATION).forEach(([field, range]) => {
      const val = editValues[field]
      if (val === null || val === undefined || val === '') {
        errors[field] = t('goals.errors.required', 'Required')
      } else if (isNaN(val)) {
        errors[field] = t('goals.errors.mustBeNumber', 'Must be a number')
      } else if (val < range.min || val > range.max) {
        errors[field] = t('goals.errors.range', '{{min}}-{{max}}', { min: range.min, max: range.max })
      } else if (val < 0) {
        errors[field] = t('goals.errors.mustBePositive', 'Must be a positive number')
      }
    })
    return errors
  }

  const saveEdit = () => {
    if (!currentGoal) return
    const errors = validateEditValues()
    setEditErrors(errors)
    if (Object.keys(errors).length > 0) return
    updateGoalMutation.mutate({
      id: currentGoal.id,
      data: editValues
    })
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return ''
    return new Date(dateStr).toLocaleDateString()
  }

  const daysSince = (dateStr) => {
    if (!dateStr) return 0
    const start = new Date(dateStr)
    const now = new Date()
    return Math.floor((now - start) / (1000 * 60 * 60 * 24))
  }

  if (isLoading) {
    return (
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">
            {t('profile.nutritionGoals', 'Nutrition Goals')}
          </h3>
        </div>
        <div className="card-body">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/2"></div>
            <div className="h-24 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="card">
      <div className="card-header flex justify-between items-center">
        <div className="flex items-center">
          <FlagIcon className="h-6 w-6 text-indigo-600 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">
            {t('profile.nutritionGoals', 'Nutrition Goals')}
          </h3>
        </div>
        {currentGoal && !editing && (
          <button
            onClick={startEdit}
            className="btn-secondary flex items-center"
          >
            <PencilIcon className="h-4 w-4 mr-1" />
            {t('common.edit', 'Edit')}
          </button>
        )}
      </div>
      <div className="card-body">
        {!currentGoal ? (
          <div className="text-center py-8">
            <FlagIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">
              {t('profile.noGoalSet', 'No nutrition goal set yet')}
            </p>
            <p className="text-sm text-gray-400 mt-1">
              {t('profile.useCalculator', 'Use the Goal Calculator to set your first goal')}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Goal Type Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-2xl mr-2">
                  {GOAL_TYPE_ICONS[currentGoal.goal_type] || '🎯'}
                </span>
                <div>
                  <p className="font-semibold text-gray-900">
                    {t(`profile.goalTypes.${currentGoal.goal_type}`, currentGoal.goal_type)}
                  </p>
                  <p className="text-xs text-gray-500 flex items-center">
                    <ClockIcon className="h-3 w-3 mr-1" />
                    {t('profile.activeSince', 'Active since')} {formatDate(currentGoal.effective_from)}
                    {' '}({daysSince(currentGoal.effective_from)} {t('common.days', 'days')})
                  </p>
                </div>
              </div>
              <span className={`px-2 py-1 rounded text-xs font-medium ${
                currentGoal.source === 'calculated' ? 'bg-blue-100 text-blue-700' :
                currentGoal.source === 'ai_suggested' ? 'bg-purple-100 text-purple-700' :
                currentGoal.source === 'health_cycle' ? 'bg-green-100 text-green-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {t(`profile.goalSources.${currentGoal.source}`, currentGoal.source)}
              </span>
            </div>

            {/* VH-231: goal set by finalizing a health cycle */}
            {currentGoal.source === 'health_cycle' && (
              <p className="text-xs text-gray-600" data-testid="goal-from-cycle">
                {currentGoal.health_cycle
                  ? t('profile.fromHealthCycle', { date: currentGoal.health_cycle.blood_test_date })
                  : t('profile.fromDeletedHealthCycle')}
                {currentGoal.health_cycle && (
                  <>
                    {' '}
                    <Link to={`/health-cycle/cycles/${currentGoal.health_cycle.cycle_id}`} className="text-indigo-600 hover:text-indigo-800">
                      {t('profile.openHealthCycle')}
                    </Link>
                  </>
                )}
              </p>
            )}

            {/* Macro Targets */}
            {editing ? (
              <div className="space-y-4">
                <div>
                  <label className="form-label">{t('profile.targetCalories', 'Daily Calories')}</label>
                  <input
                    type="number"
                    value={editValues.target_calories || ''}
                    onChange={(e) => {
                      setEditValues({ ...editValues, target_calories: parseInt(e.target.value) })
                      setEditErrors((prev) => ({ ...prev, target_calories: null }))
                    }}
                    className={`form-input ${editErrors.target_calories ? 'border-red-400' : ''}`}
                  />
                  {editErrors.target_calories && (
                    <p className="text-xs text-red-600 mt-1 flex items-center">
                      <ExclamationCircleIcon className="h-3.5 w-3.5 mr-1" />{editErrors.target_calories}
                    </p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">{t('goals.errors.range', '{{min}}-{{max}}', { min: 500, max: 10000 })}</p>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { key: 'target_protein_g', label: t('profile.protein', 'Protein (g)') },
                    { key: 'target_carbs_g', label: t('profile.carbs', 'Carbs (g)') },
                    { key: 'target_fat_g', label: t('profile.fat', 'Fat (g)') }
                  ].map(({ key, label }) => (
                    <div key={key}>
                      <label className="form-label">{label}</label>
                      <input
                        type="number"
                        value={editValues[key] || ''}
                        onChange={(e) => {
                          setEditValues({ ...editValues, [key]: parseFloat(e.target.value) })
                          setEditErrors((prev) => ({ ...prev, [key]: null }))
                        }}
                        className={`form-input ${editErrors[key] ? 'border-red-400' : ''}`}
                        step="0.1"
                      />
                      {editErrors[key] && (
                        <p className="text-xs text-red-600 mt-1 flex items-center">
                          <ExclamationCircleIcon className="h-3.5 w-3.5 mr-1" />{editErrors[key]}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex justify-end space-x-2">
                  <button onClick={cancelEdit} className="btn-secondary">
                    <XMarkIcon className="h-4 w-4 mr-1" />
                    {t('common.cancel', 'Cancel')}
                  </button>
                  <button
                    onClick={saveEdit}
                    disabled={updateGoalMutation.isPending}
                    className="btn-primary"
                  >
                    <CheckIcon className="h-4 w-4 mr-1" />
                    {updateGoalMutation.isPending ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-orange-50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-orange-600">
                    {currentGoal.target_calories || '-'}
                  </p>
                  <p className="text-sm text-gray-600">{t('common.kcal', 'kcal')}</p>
                </div>
                <div className="space-y-2">
                  <div className="bg-red-50 rounded-lg px-3 py-2 flex justify-between items-center">
                    <span className="text-sm text-gray-600">{t('profile.protein', 'Protein')}</span>
                    <span className="font-semibold text-red-700">
                      {currentGoal.target_protein_g?.toFixed(0) || '-'}g
                    </span>
                  </div>
                  <div className="bg-yellow-50 rounded-lg px-3 py-2 flex justify-between items-center">
                    <span className="text-sm text-gray-600">{t('profile.carbs', 'Carbs')}</span>
                    <span className="font-semibold text-yellow-700">
                      {currentGoal.target_carbs_g?.toFixed(0) || '-'}g
                    </span>
                  </div>
                  {currentGoal.target_carbs_training_day_g != null && (
                    <div className="bg-yellow-50 rounded-lg px-3 py-2 flex justify-between items-center">
                      <span className="text-sm text-gray-600">{t('profile.carbsTrainingDay')}</span>
                      <span className="font-semibold text-yellow-700">
                        {currentGoal.target_carbs_training_day_g.toFixed(0)}g
                      </span>
                    </div>
                  )}
                  {currentGoal.target_fiber_g != null && (
                    <div className="bg-green-50 rounded-lg px-3 py-2 flex justify-between items-center">
                      <span className="text-sm text-gray-600">{t('profile.fiber')}</span>
                      <span className="font-semibold text-green-700">
                        {currentGoal.target_fiber_g.toFixed(0)}g
                      </span>
                    </div>
                  )}
                  <div className="bg-blue-50 rounded-lg px-3 py-2 flex justify-between items-center">
                    <span className="text-sm text-gray-600">{t('profile.fat', 'Fat')}</span>
                    <span className="font-semibold text-blue-700">
                      {currentGoal.target_fat_g?.toFixed(0) || '-'}g
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Activity Level & Target Weight */}
            {(currentGoal.activity_level || currentGoal.target_weight_kg) && (
              <div className="border-t pt-4 text-sm text-gray-600">
                {currentGoal.activity_level && (
                  <p>
                    {t('profile.activityLevel', 'Activity')}: {' '}
                    {t(`profile.activityLevels.${currentGoal.activity_level}`, currentGoal.activity_level)}
                  </p>
                )}
                {currentGoal.target_weight_kg && (
                  <p>
                    {t('profile.targetWeight', 'Target Weight')}: {currentGoal.target_weight_kg}kg
                  </p>
                )}
                {currentGoal.weekly_weight_change_kg && (
                  <p>
                    {t('profile.weeklyChange', 'Weekly Change')}: {currentGoal.weekly_weight_change_kg > 0 ? '+' : ''}{currentGoal.weekly_weight_change_kg}kg
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
